"use client";

import { useEffect, useRef } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  fetchGenerationById,
  getPendingGenerationsFromStorage,
  markPendingGenerationNotifiedInStorage,
  removePendingGenerationFromStorage,
} from "@/hooks/use-generation";
import { useInvalidateCredits } from "@/hooks/use-credits";

/**
 * Background watcher that polls localStorage for pending generation IDs and
 * surfaces a toast with a "View" CTA when any generation completes (or a
 * failure toast when it fails). Designed to be mounted at the root layout
 * so it runs globally regardless of which page the user is on.
 */
export function GenerationCompletionWatcher() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const invalidateCredits = useInvalidateCredits();
  const notifiedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (typeof window === "undefined") return;

    let isCancelled = false;

    const checkPendingGenerations = async () => {
      if (isCancelled) return;

      const pendingEntries = getPendingGenerationsFromStorage();
      if (!pendingEntries.length) return;

      for (const entry of pendingEntries) {
        if (isCancelled) return;

        // Skip if already notified in this session or marked in storage.
        if (notifiedIdsRef.current.has(entry.id) || entry.notified) {
          continue;
        }

        try {
          const data = await fetchGenerationById(entry.id);

          const hasErrorParam = !!searchParams.get("error");
          const isOnAppPage = pathname === "/app" || pathname.startsWith("/app/");
          const isOnResultsPage = pathname.startsWith("/results/");
          const isOnCurrentResult = pathname === `/results/${data.id}`;
          const canToast = !hasErrorParam && !isOnAppPage && !isOnResultsPage;

          if (data.status === "completed" && data.resultImageUrl) {
            notifiedIdsRef.current.add(entry.id);
            markPendingGenerationNotifiedInStorage(entry.id);

            queryClient.invalidateQueries({ queryKey: ["generations"] });
            invalidateCredits();

            if (isOnCurrentResult) {
              removePendingGenerationFromStorage(entry.id);
              continue;
            }

            if (canToast) {
              toast.success("Your wheel preview is ready", {
                duration: 10_000,
                action: {
                  label: "View",
                  onClick: () => {
                    removePendingGenerationFromStorage(entry.id);
                    // Defer navigation to avoid DOM removeChild errors during unmount.
                    window.setTimeout(() => {
                      router.push(`/results/${data.id}`);
                    }, 100);
                  },
                },
              });
            }
          } else if (data.status === "failed") {
            notifiedIdsRef.current.add(entry.id);
            markPendingGenerationNotifiedInStorage(entry.id);
            removePendingGenerationFromStorage(entry.id);

            if (canToast) {
              toast.error("Your wheel preview failed. Please try again.", {
                duration: 10_000,
              });
            }
          }
        } catch (error) {
          console.warn("Failed to check generation status", {
            generationId: entry.id,
            error,
          });
        }
      }
    };

    // Check immediately on mount, then poll every 5 seconds.
    void checkPendingGenerations();
    const intervalId = window.setInterval(() => {
      void checkPendingGenerations();
    }, 5000);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [queryClient, invalidateCredits, pathname, router, searchParams]);

  // Renders nothing; purely side-effect component.
  return null;
}
